<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'

const props = defineProps<{
  strings: string[]
  pauseDuration?: number
}>()

const pauseDuration = props.pauseDuration ?? 2000

const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

// SSR renders the first string in full (SEO + no hydration mismatch);
// GSAP ScrambleText then decodes each next role in place.
const displayRef = useTemplateRef<HTMLElement>('display')

let timeout: ReturnType<typeof setTimeout>
let killTween: (() => void) | undefined

onMounted(async () => {
  if (prefersReducedMotion.value || props.strings.length < 2) return

  const [{ gsap }, { ScrambleTextPlugin }] = await Promise.all([
    import('gsap'),
    import('gsap/ScrambleTextPlugin'),
  ])
  gsap.registerPlugin(ScrambleTextPlugin)

  const el = displayRef.value
  if (!el) return
  killTween = () => gsap.killTweensOf(el)

  let index = 0
  function next() {
    index = (index + 1) % props.strings.length
    gsap.to(el, {
      duration: 1.1,
      scrambleText: {
        text: props.strings[index]!,
        chars: '!<>-_\\/[]{}—=+*^?#',
        speed: 0.4,
      },
      onComplete: () => {
        timeout = setTimeout(next, pauseDuration)
      },
    })
  }

  timeout = setTimeout(next, pauseDuration)
})

onUnmounted(() => {
  clearTimeout(timeout)
  killTween?.()
})
</script>

<template>
  <span><span ref="display">{{ strings[0] }}</span><span class="typed-cursor text-[var(--color-amber)]">|</span></span>
</template>
